import * as React from 'react'

import MagicWandIcon from '@bufferapp/ui/Icon/Icons/MagicWand'

import {
  Button,
  Flex,
  ScrollArea,
  Skeleton,
  Tooltip,
} from '@buffer-mono/popcorn'

import type {
  SavedReply,
  Suggestion as GraphQLSuggestion,
} from '~publish/gql/graphql'

import { SuggestionsPaywall } from './SuggestionsPaywall'
import { useCommentData } from '../context/CommentContext'
import { useSavedReplies } from '../hooks/savedReplies/useSavedReplies'
import { useCommentsTracking } from '../hooks/useCommentsTracking'
import { useCommentSuggestions } from '../hooks/useCommentSuggestions'
import { safeSlice } from '../util/safeSlice'
import { findMostSimilarSavedReply } from '../util/savedRepliesHelpers'

import styles from './SuggestionButtons.module.css'

export type Suggestion = GraphQLSuggestion & {
  id: string
}

/**
 * Checks if a comment text is meaningful enough to generate AI suggestions
 * @param text The comment text to validate
 * @returns boolean indicating if the text is meaningful
 */
const isMeaningfulComment = (text: string): boolean => {
  // Check if the text contains any letters, numbers, or emojis
  // This includes:
  // - Basic Latin letters and numbers
  // - Emojis and other Unicode symbols
  // - Non-Latin scripts
  return /[\p{L}\p{N}\p{Emoji}]/u.test(text)
}

/**
 * Skeleton loading component for suggestion buttons
 */
const SuggestionButtonSkeleton = ({
  width = 120,
}: {
  width?: number
}): JSX.Element => (
  <Skeleton
    width={width}
    height="var(--control-size-sm)"
    rounded
    className={styles.skeletonButton}
  />
)

/**
 * Skeleton loading for AI suggestions
 */
const AISuggestionsSkeleton = (): JSX.Element => (
  <>
    <SuggestionButtonSkeleton width={110} />
    <SuggestionButtonSkeleton width={90} />
    <SuggestionButtonSkeleton width={130} />
    <SuggestionButtonSkeleton width={95} />
    <SuggestionButtonSkeleton width={150} />
  </>
)

/**
 * Skeleton loading for saved reply
 */
const SavedReplySkeleton = (): JSX.Element => (
  <SuggestionButtonSkeleton width={140} />
)

/**
 * Skeleton loading for suggestions
 */
const SuggestionButtonsSkeleton = (): JSX.Element => (
  <Flex direction="row" gap="2xs" wrap="nowrap">
    <SavedReplySkeleton />
    <AISuggestionsSkeleton />
  </Flex>
)

type SuggestionButtonsProps = {
  /**
   * Callback when a suggestion is clicked
   */
  onSuggestionClick: (suggestionText: string) => void
}

export function SuggestionButtons({
  onSuggestionClick,
}: SuggestionButtonsProps): JSX.Element {
  const comment = useCommentData()
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>()
  const [mostSimilarSavedReply, setMostSimilarSavedReply] =
    React.useState<SavedReply | null>(null)
  const [isSuggestionsLoading, setIsSuggestionsLoading] = React.useState(false)
  const [isMostSimilarSavedReplyLoading, setIsMostSimilarSavedReplyLoading] =
    React.useState(false)
  const [isReachLimitError, setIsReachLimitError] =
    React.useState<boolean>(false)
  const { fetchCommentSuggestions } = useCommentSuggestions()
  const { savedReplies } = useSavedReplies()
  const { trackSuggestionUsed, trackSavedReplyUsed } = useCommentsTracking()

  const commentId = comment.id
  const commentText = comment.text

  React.useEffect(() => {
    if (!commentId || !commentText) return

    /**
     * Fetch suggestions from AI
     */
    const fetchSuggestions = async (): Promise<void> => {
      setIsSuggestionsLoading(true)

      try {
        const { data, error } = await fetchCommentSuggestions({
          commentId,
          regenerate: false,
        })

        if (data) {
          // Generate unique IDs for each suggestion when received
          const suggestionsWithIds: Suggestion[] = data.map((s) => ({
            ...s,
            id: crypto.randomUUID(),
          }))
          setSuggestions(suggestionsWithIds)
        }
        if (error) {
          setIsReachLimitError(true)
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error)
      } finally {
        setIsSuggestionsLoading(false)
      }
    }

    /**
     * Get the saved replied most similar to the comment text
     * using cosine similarity method
     */
    const findClosestSavedReply = async (): Promise<void> => {
      if (!savedReplies || !savedReplies.length) {
        return
      }

      try {
        setIsMostSimilarSavedReplyLoading(true)
        const closestSavedReply = await findMostSimilarSavedReply({
          commentText,
          savedReplies,
        })

        if (closestSavedReply) {
          setMostSimilarSavedReply(closestSavedReply)
        }
      } catch (error) {
        console.error('Error finding similar saved replies:', error)
      } finally {
        setIsMostSimilarSavedReplyLoading(false)
      }
    }

    fetchSuggestions()
    findClosestSavedReply()
  }, [commentId, commentText, fetchCommentSuggestions])

  const handleSuggestionClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, suggestionText: string): void => {
      e.preventDefault()
      onSuggestionClick(suggestionText)

      trackSuggestionUsed({
        comment,
        suggestionText,
      })
    },
    [comment, onSuggestionClick, trackSuggestionUsed],
  )

  const handleSavedReplyClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, savedReply: SavedReply): void => {
      e.preventDefault()
      onSuggestionClick(savedReply.content.text)

      trackSavedReplyUsed({
        comment,
        savedReply,
      })
    },
    [comment, onSuggestionClick, trackSavedReplyUsed],
  )

  if (!commentText || !commentId || !isMeaningfulComment(commentText)) {
    return <></>
  }

  if (isMostSimilarSavedReplyLoading && isSuggestionsLoading) {
    return (
      <ScrollArea
        scrollbars="horizontal"
        visibility="hover"
        gradientFade={50}
        size="small"
        className={styles.suggestionButtons}
        aria-label="Suggestion buttons"
      >
        <SuggestionButtonsSkeleton />
      </ScrollArea>
    )
  }

  if (isReachLimitError) {
    return (
      <SuggestionsPaywall
        ctaProperties={{
          location: 'comments',
          button: 'generateReplySuggestions',
        }}
      >
        <Button variant="tertiary" className={styles.generateReplySuggestions}>
          <MagicWandIcon />
          Reply suggestions
        </Button>
      </SuggestionsPaywall>
    )
  }

  return (
    <ScrollArea
      scrollbars="horizontal"
      visibility="hover"
      gradientFade={50}
      size="small"
      className={styles.suggestionButtons}
      aria-label="Suggestion buttons"
    >
      <Flex direction="row" gap="2xs" wrap="nowrap">
        {isMostSimilarSavedReplyLoading ? (
          <SavedReplySkeleton />
        ) : (
          mostSimilarSavedReply && (
            <Tooltip content={mostSimilarSavedReply.content.text}>
              <Button
                variant="tertiary"
                size="small"
                className={styles.closestSavedReply}
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
                  handleSavedReplyClick(e, mostSimilarSavedReply)
                }
              >
                {safeSlice(mostSimilarSavedReply.content.text, 25)}
              </Button>
            </Tooltip>
          )
        )}
        {isSuggestionsLoading ? (
          <AISuggestionsSkeleton />
        ) : (
          suggestions &&
          suggestions?.map((quickReply: Suggestion) => (
            <Tooltip key={quickReply.id} content={quickReply.text}>
              <Button
                variant="tertiary"
                size="small"
                className={styles.suggestionButton}
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
                  handleSuggestionClick(e, quickReply.text)
                }
              >
                {safeSlice(quickReply.text, 25)}
              </Button>
            </Tooltip>
          ))
        )}
      </Flex>
    </ScrollArea>
  )
}
